import { Close, Delete, Save, ShowChart } from '@mui/icons-material';
import {
  Box,
  Button,
  CardActions,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import { ReactElement, useEffect, useState } from 'react';
import '../../../../../Styles/Colors.scss';
import { MatrixSavedParameters } from '../../../../../Types/MatrixSavedParameters';
import { MarkItViewQueryParameters } from '../../../../../Types/MarkItViewQueryParameters';
import { MarkItViewQuery } from '../../../../../Types/MarkItViewQueryResult';
import { MarkItViewSavedQuerySet } from '../../../../../Types/MarkItViewSavedQuerySet';
import { CalculateMatrixResponse } from '../../../../../Generated/Mark-It-View-Api';
import { markItViewQuerySchema } from '../../../../../Validations/queryFormValidation';
import MatrixForm from '../MatrixForm/MatrixForm';
import QueryForm from '../QueryForm/QueryForm';
import { usePrevious } from '../../../../../Hooks';
import { StoneXButton } from '../../../../StoneXMui';
import { MarkItViewApi } from '../../../../../Apis/Apis';
import { EmailScheduleDraft } from '../../../../../Types/EmailScheduleDraft';
import { createEmptyEmailScheduleDraft, hasEmailScheduleErrors, validateEmailScheduleDraft } from '../../../EmailSchedules/EmailScheduleDraft';
import EmailScheduleFields from '../../../EmailSchedules/EmailScheduleFields';

interface SaveQuerySetParameterDialogProps {
  open: boolean;
  onClose: () => void;
  querySet: MarkItViewSavedQuerySet;
  save: (querySetToSave: MarkItViewSavedQuerySet) => Promise<void>;
}

function SaveQuerySetDialog(props: SaveQuerySetParameterDialogProps) {
  const [isEmpty, setIsEmpty] = useState<boolean>();
  const [totalQueries, setTotalQueries] = useState<number>(0);
  const [numberOfInvalidQueries, setNumberOfInvalidQueries] = useState<number>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [queryName, setQueryName] = useState(props.querySet.name);
  const [isSaving, setIsSaving] = useState(false);
  const [errorSaving, setErrorSaving] = useState(false);
  const [emailSchedule, setEmailSchedule] = useState<EmailScheduleDraft>(props.querySet.emailSchedule ?? createEmptyEmailScheduleDraft());
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testSendResult, setTestSendResult] = useState<string>();

  const emailScheduleErrors = validateEmailScheduleDraft(emailSchedule);
  const canSendTest = emailSchedule.enabled && emailSchedule.emailScheduleId > 0;

  useEffect(() => {
    if (props.open) {
      setQueryName(props.querySet.name);
      setEmailSchedule(props.querySet.emailSchedule ?? createEmptyEmailScheduleDraft());
      setTestSendResult(undefined);
    }

    setTotalQueries(props.querySet.querySetParameters.length);
    const hasNoQueries = props.querySet.querySetParameters.length === 0;
    const hasNoMatrix = !props.querySet.matrixParameters;
    setIsEmpty(hasNoQueries && hasNoMatrix);
    const filtered = props.querySet.querySetParameters.filter((q) => {
      try {
        markItViewQuerySchema.validateSync(q);
        return true;
      } catch {
        return false;
      }
    });
    setNumberOfInvalidQueries(props.querySet.querySetParameters.length - filtered.length);
  }, [props.open]);

  const hasError = (): boolean => {
    return isEmpty || numberOfInvalidQueries! >= 1;
  };

  const sendTestEmail = () => {
    setIsSendingTest(true);
    setTestSendResult(undefined);
    MarkItViewApi.sendEmailScheduleNow(undefined, String(emailSchedule.emailScheduleId))
      .then((response) => {
        setTestSendResult(response.data.sent ? 'Test email sent.' : `Test email failed: ${response.data.error ?? 'unknown error'}`);
      })
      .catch(() => setTestSendResult('Test email failed to send.'))
      .finally(() => setIsSendingTest(false));
  };

  const save = () => {
    const itemToSave = { ...props.querySet, name: queryName, id: props.querySet.id, emailSchedule };
    //TODO: Use loading state hook to make this simpler!
    setErrorSaving(false);
    setIsSaving(true);
    props
      .save(itemToSave)
      .then((q) => {
        setIsSaving(false);
        props.onClose();
      })
      .catch((err) => {
        setErrorSaving(true);
        setIsSaving(false);
      });
  };

  const onQueryNameChange = (e: any): void => {
    const name = e.target.value?.trimStart();
    setQueryName(name);
    if (!name || name.length === 0) {
      setErrorMessage('Please enter a name');
    } else if (name.legnth > 100) {
      setErrorMessage('Name cannot exceed 100 characters');
    } else {
      setErrorMessage(undefined);
    }
  };

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      if (!errorMessage && !hasEmailScheduleErrors(emailScheduleErrors)) {
        save();
      }
    }
  };

  const renderErrorDialog = () => {
    return (
      <Dialog open={props.open} onClose={props.onClose}>
        <DialogTitle>Save Mark-It-View Query</DialogTitle>
        <DialogContent>
          {isEmpty && <DialogContentText>There are no queries to save.</DialogContentText>}
          {numberOfInvalidQueries == 1 && totalQueries == 1 && <DialogContentText>Your query is invalid. Please correct it and try again.</DialogContentText>}
          {numberOfInvalidQueries == 1 && totalQueries > 1 && <DialogContentText>One of your queries. Please correct it and try again.</DialogContentText>}
          {numberOfInvalidQueries! > 1 && totalQueries > 1 && (
            <DialogContentText>
              {numberOfInvalidQueries} of your {totalQueries} queries are invalid. Please correct them and try again.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onClose}>Ok</Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderSavePrompt = () => {
    return (
      <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="sm">
        <DialogTitle>Save Mark-It-View Query</DialogTitle>
        <DialogContent>
          <DialogContentText>Please enter or update query name.</DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            error={!!errorMessage}
            helperText={errorMessage}
            label="Saved Query Name"
            type="text"
            fullWidth
            variant="standard"
            value={queryName}
            onChange={onQueryNameChange}
            onKeyDown={handleKeyDown}
            required
          />
          <EmailScheduleFields draft={emailSchedule} errors={emailScheduleErrors} disabled={isSaving} onChange={setEmailSchedule} />
          {testSendResult && <DialogContentText sx={{ mt: 1 }}>{testSendResult}</DialogContentText>}
        </DialogContent>
        {errorSaving && <DialogContentText sx={{ px: 3 }} color="error">An error occured while saving this query.</DialogContentText>}
        <DialogActions>
          {canSendTest && (
            <StoneXButton onClick={sendTestEmail} isWorking={isSendingTest} disabled={isSaving} sx={{ marginRight: 'auto' }}>
              Send test email
            </StoneXButton>
          )}
          <Button onClick={props.onClose}>Cancel</Button>
          <StoneXButton onClick={save} disabled={!!errorMessage || hasEmailScheduleErrors(emailScheduleErrors)} isWorking={isSaving} >
            Save
          </StoneXButton>
        </DialogActions>
      </Dialog>
    );
  };

  return hasError() ? renderErrorDialog() : renderSavePrompt();
}

interface QueryFormListProps {
  onQueriesUpdate: (queries: MarkItViewQuery[]) => void;
  onQueryParamsUpdate: (queryParams: MarkItViewSavedQuerySet) => void;
  onMatrixResultUpdate: (result: CalculateMatrixResponse | null) => void;
  querySet?: MarkItViewSavedQuerySet;
  saveQuerySet: (querySetToSave: MarkItViewSavedQuerySet) => Promise<void>;
}

export default function QueryFormList(props: QueryFormListProps) {
  const [forms, setForms] = useState<ReactElement[]>([]);
  const [queries, setQueries] = useState<MarkItViewQuery[]>([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [queryNameDialogOpen, setQueryNameDialogOpen] = useState(false);
  const [querySet, setQuerySet] = useState<MarkItViewSavedQuerySet>(props.querySet ?? { id: 0, name: '', querySetParameters: [] });
  const [hasMatrix, setHasMatrix] = useState<boolean>(!!props.querySet?.matrixParameters);
  const [matrixParams, setMatrixParams] = useState<MatrixSavedParameters | null>(props.querySet?.matrixParameters ?? null);
  const [matrixLoading, setMatrixLoading] = useState<boolean>(false);

  const onTabChange = (event: React.SyntheticEvent, newTabIndex: number) => {
    setActiveTabIndex(newTabIndex);
  };

  const deleteTab = (formId: number) => {
    setQueries((previous) => previous.filter((q, i) => q.id !== formId));
    setForms((previous) => previous.filter((q, i) => q.props.id !== formId));

    if (forms.length == 1 && activeTabIndex == 1) {
      setActiveTabIndex(0);
    }
  };

  const onQueryUpdate = (query: MarkItViewQuery) => {
    setQueries((previous) => {
        const updatedList = previous.map((y) => {
          if (y.id == query.id) {
            return query;
          } else {
            return y;
          }
        });

        const queryExists = previous.find((x) => x.id == query.id);
        if (!queryExists) {
          updatedList.push(query);
        }

      return updatedList;
    });
  };

  const onQueryParamsUpdate = (query: MarkItViewQueryParameters): void => {
    setQuerySet((prev) => ({
      ...prev,
      querySetParameters: [...prev.querySetParameters.filter((q) => q.id !== query.id), query],
    }));
  };

  const createNewForm = (savedQuery?: MarkItViewQueryParameters) => {
    const id = savedQuery?.id ?? new Date().valueOf() + Math.random();
    const form = (
      <QueryForm
        key={id}
        id={id}
        onUpdateQuery={onQueryUpdate}
        savedQueryToLoad={savedQuery}
        onUpdateQueryParams={onQueryParamsUpdate}
      />
    );
    setForms((previous) => [...previous, form]);
  };

  const renderFormTabs = () => {
    return forms.map((form, index) => {
      const query = queries.find((q) => q.id == form.props.id);
      return (
        <Tab
          label={
            <span style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: query?.loadingState.failed() ? 'red' : 'inherit' }}>
                {query?.product?.name ?? 'New Query'}
              </span>
              {query?.loadingState.isLoading() ? (
                <CircularProgress size="1.25em" />
              ) : (
                <Close
                  fontSize="small"
                  sx={{ color: 'gray', '&:hover': { color: 'inherit' } }}
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteTab(form.props.id);
                  }}
                />
              )}
            </span>
          }
          key={form.props.id}
        ></Tab>
      );
    });
  };

  const renderMatrixTab = () => {
    if (!hasMatrix) { return null; }
    return (
      <Tab
        label={
          <span style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Matrix
            </span>
            {matrixLoading ? (
              <CircularProgress size="1.25em" />
            ) : (
              <Close
                fontSize="small"
                sx={{ color: 'gray', '&:hover': { color: 'inherit' } }}
                onClick={(e) => {
                  e.stopPropagation();
                  setHasMatrix(false);
                  setMatrixParams(null);
                  props.onMatrixResultUpdate(null);
                  if (activeTabIndex === matrixTabIndex) {
                    setActiveTabIndex(0);
                  }
                }}
              />
            )}
          </span>
        }
        key="matrix"
      />
    );
  };

  const renderForms = () =>
    forms.map((queryForm, i) => (
      <Box sx={{ display: activeTabIndex == i ? 'flex' : 'none', flexDirection: 'column' }} key={queryForm.props.id}>
        {queryForm}
      </Box>
    ));

  useEffect(() => {
    if (forms.length == 0) {
      if (props.querySet) {
        props.querySet.querySetParameters.forEach((q) => createNewForm(q));
      } else {
        createNewForm();
      }
    }
  }, []);

  useEffect(() => {
    props.onQueriesUpdate(queries.filter((q) => q.query != undefined));
  }, [queries]);

  useEffect(() => {
    props.onQueryParamsUpdate(querySet);
  }, [querySet]);

  // Update querySet matrix params when they change
  useEffect(() => {
    setQuerySet((prev) => ({ ...prev, matrixParameters: matrixParams }));
  }, [matrixParams]);

  const matrixTabIndex = forms.length;

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: 300, flexGrow: 0, flexShrink: 0 }}>
        <Tabs orientation="vertical" variant="scrollable" value={activeTabIndex} onChange={onTabChange} sx={{ borderRight: 1, borderColor: 'divider', marginTop: '20px'}}>
          {renderFormTabs()}
          {renderMatrixTab()}
          <Tab label="Add More" key="new-query" onClick={() => createNewForm()}></Tab>
          {!hasMatrix && (
            <Tab
              label="Add Matrix"
              key="add-matrix"
              icon={<ShowChart fontSize="small" />}
              iconPosition="start"
              onClick={() => { setHasMatrix(true); setActiveTabIndex(matrixTabIndex); }}
            />
          )}
        </Tabs>
        <CardActions>
          <Button variant="outlined" onClick={() => setQueryNameDialogOpen(true)} startIcon={<Save />}>
            Save
          </Button>
        </CardActions>
      </Box>

      <SaveQuerySetDialog querySet={querySet} open={queryNameDialogOpen} onClose={() => setQueryNameDialogOpen(false)} save={props.saveQuerySet} />

      {renderForms()}

      {hasMatrix && (
        <Box sx={{ display: activeTabIndex === matrixTabIndex ? 'flex' : 'none', flexDirection: 'column' }}>
          <MatrixForm
            savedParams={props.querySet?.matrixParameters}
            onMatrixResult={props.onMatrixResultUpdate}
            onParamsUpdate={setMatrixParams}
            onLoadingChange={setMatrixLoading}
          />
        </Box>
      )}
    </>
  );
}
